import { useEffect, useState } from "react";
import { toast } from "sonner";
import { User, Package, ShoppingBag, Search, StickyNote, RefreshCw, Send, SendHorizontal } from "lucide-react";
import api, { errText, fileUrl } from "@/lib/api";
import { OrderTemplateDialog } from "@/components/inbox/OrderTemplateDialog";

const TABS = [
  { id: "customer", label: "Customer", icon: User },
  { id: "products", label: "Products", icon: Package },
  { id: "orders", label: "Orders", icon: ShoppingBag },
];

const ORDER_COLORS = { processing: "badge-pending", completed: "badge-approved", cancelled: "badge-rejected", pending: "badge-pending", shipped: "badge-approved", refunded: "badge-rejected" };

export default function ContextPanel({ conv }) {
  const [tab, setTab] = useState("customer");
  const [info, setInfo] = useState(null);
  const [note, setNote] = useState("");
  const [products, setProducts] = useState([]);
  const [prodSearch, setProdSearch] = useState("");
  const [orderQ, setOrderQ] = useState("");
  const [orders, setOrders] = useState(null);
  const [orderErr, setOrderErr] = useState("");
  const [orderBusy, setOrderBusy] = useState(false);
  const [recent, setRecent] = useState({ loading: true, orders: [], snippetRequired: false, error: null });
  const [updateOrder, setUpdateOrder] = useState(null);

  const loadRecentOrders = async (refresh = false) => {
    setRecent((r) => ({ ...r, loading: true }));
    try {
      const { data } = await api.get(`/orders/by-phone/${conv.phone}`, { params: refresh ? { refresh: true } : {} });
      setRecent({ loading: false, orders: data.orders || [], snippetRequired: !!data.snippet_required, error: data.error || null, configured: data.configured !== false });
    } catch (e) {
      setRecent({ loading: false, orders: [], snippetRequired: false, error: errText(e) });
    }
  };

  useEffect(() => {
    api.get(`/contacts/by-phone/${conv.phone}`).then((r) => setInfo(r.data)).catch(() => {});
    loadRecentOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conv.phone]);

  useEffect(() => {
    if (tab === "products") api.get("/products", { params: { search: prodSearch } }).then((r) => setProducts(r.data)).catch(() => {});
  }, [tab, prodSearch]);

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      const { data } = await api.post(`/contacts/by-phone/${conv.phone}/notes`, { text: note });
      setInfo((i) => ({ ...i, notes: [data, ...(i?.notes || [])] }));
      setNote("");
      toast.success("Note added");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const sendPhotoToChat = async (fileId, caption) => {
    try {
      await api.post(`/conversations/${conv.id}/send-file`, { file_id: fileId, caption });
      toast.success("Photo sent to chat");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const searchOrders = async () => {
    if (!orderQ.trim()) return;
    setOrderBusy(true);
    setOrderErr("");
    setOrders(null);
    try {
      const { data } = await api.get("/orders/search", { params: { q: orderQ } });
      setOrders(data);
    } catch (e) {
      setOrderErr(errText(e));
    } finally {
      setOrderBusy(false);
    }
  };

  return (
    <div className="w-80 shrink-0 border-l-2 flex flex-col bg-white/50" style={{ borderColor: "var(--cd-navy)" }}>
      <div className="flex border-b-2" style={{ borderColor: "var(--cd-navy)" }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`context-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className="flex-1 py-2.5 text-xs font-bold font-heading flex items-center justify-center gap-1.5 transition-colors"
            style={tab === t.id ? { background: "var(--cd-navy)", color: "#fff" } : {}}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {tab === "customer" && (
          <>
            <div className="card-tactile card-yellow p-3" data-testid="customer-info-card">
              <div className="font-heading font-bold">{info?.contact?.name || conv.contact_name}</div>
              <div className="text-xs font-mono2 text-slate-600">+{conv.phone}</div>
              {info?.contact?.email && <div className="text-xs text-slate-600">{info.contact.email}</div>}
              <div className="flex flex-wrap gap-1 mt-2">
                {(info?.contact?.tags || []).map((t) => (
                  <span key={t} className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white border" style={{ borderColor: "var(--cd-navy)" }}>{t}</span>
                ))}
              </div>
            </div>
            <div className="card-tactile card-blue p-3">
              <div className="label-upper">History with us</div>
              <div className="text-xs font-semibold">{info?.conversations?.length ?? 0} conversation(s) · {info?.message_count ?? 0} messages</div>
              {(info?.conversations || []).slice(0, 5).map((c) => (
                <div key={c.id} className="text-xs mt-1.5 flex justify-between">
                  <span>{new Date(c.created_at).toLocaleDateString()}</span>
                  <span className={`status-badge ${c.status === "resolved" ? "badge-approved" : "badge-pending"}`}>{c.status}</span>
                </div>
              ))}
            </div>
            <div className="card-tactile card-green p-3" data-testid="recent-orders-card">
              <div className="flex items-center justify-between mb-1">
                <div className="label-upper mb-0 flex items-center gap-1"><ShoppingBag size={12} /> Recent Orders</div>
                <button data-testid="recent-orders-refresh" className="btn-ghost2 py-0.5" onClick={() => loadRecentOrders(true)} title="Refresh from store">
                  <RefreshCw size={12} className={recent.loading ? "animate-spin" : ""} />
                </button>
              </div>
              {recent.loading && <div className="font-hand text-base text-amber-800">syncing with woo store...</div>}
              {!recent.loading && recent.snippetRequired && (
                <div className="text-xs text-slate-600">Auto-sync needs the phone-lookup snippet installed on your store. Meanwhile, use the Orders tab to search manually.</div>
              )}
              {!recent.loading && recent.error && <div className="text-xs text-red-700 font-semibold">{recent.error}</div>}
              {!recent.loading && !recent.snippetRequired && !recent.error && recent.orders.length === 0 && (
                <div className="text-xs text-slate-600">No orders found for this number.</div>
              )}
              {recent.orders.map((o) => (
                <div key={o.id} className="bg-white/70 rounded-lg border px-2 py-1.5 mt-1.5" style={{ borderColor: "#E5DCB8" }} data-testid={`recent-order-${o.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-xs">#{o.number}</span>
                    <span className={`status-badge ${ORDER_COLORS[o.status] || "badge-pending"}`}>{o.status}</span>
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-slate-600 mt-0.5">
                    <span>{new Date(o.date).toLocaleDateString()}</span>
                    <span className="font-bold text-slate-800">{o.currency === "INR" ? "₹" : o.currency}{o.total}</span>
                  </div>
                  <button data-testid={`send-order-update-${o.id}`} className="btn-yellow w-full justify-center py-1 mt-1.5 text-[11px]" onClick={() => setUpdateOrder(o)}>
                    <Send size={11} /> Send Order Update
                  </button>
                </div>
              ))}
            </div>
            <div className="card-tactile p-3">
              <div className="label-upper flex items-center gap-1"><StickyNote size={12} /> Agent Notes</div>
              <div className="flex gap-1.5 mb-2">
                <input data-testid="note-input" className="input-tactile py-1 text-xs" placeholder="Add a note..." value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} />
                <button data-testid="add-note-btn" className="btn-yellow py-1 px-2 text-xs" onClick={addNote}>Add</button>
              </div>
              {(info?.notes || []).map((n) => (
                <div key={n.id} className="text-xs bg-[var(--cd-yellow)] rounded-lg p-2 mb-1.5 border" style={{ borderColor: "#E5DCB8" }}>
                  <div>{n.text}</div>
                  <div className="text-[9px] text-slate-500 mt-0.5">{n.author} · {new Date(n.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "products" && (
          <>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
              <input data-testid="product-search-input" className="input-tactile pl-8 py-1.5 text-sm" placeholder="Search product databank..." value={prodSearch} onChange={(e) => setProdSearch(e.target.value)} />
            </div>
            {products.length === 0 && <div className="font-hand text-lg text-amber-800 text-center pt-4">no products yet — admin can add them in Product Databank</div>}
            {products.map((p) => (
              <div key={p.id} className="card-tactile p-3" data-testid={`product-card-${p.id}`}>
                {p.photos?.length > 0 ? (
                  <div className="grid grid-cols-3 gap-1.5 mb-2">
                    {p.photos.map((ph) => (
                      <div key={ph.file_id} className="relative group cursor-grab" draggable
                        onDragStart={(e) => { e.dataTransfer.setData("application/x-cd-product", JSON.stringify({ file_id: ph.file_id, caption: p.name })); e.dataTransfer.effectAllowed = "copy"; }}
                        data-testid={`product-photo-${ph.file_id}`} title="Drag into chat or click send">
                        <img src={fileUrl(ph.file_id)} alt={p.name} className="w-full h-16 object-cover rounded-lg border" style={{ borderColor: "var(--cd-navy)" }} />
                        <button data-testid={`send-photo-${ph.file_id}`} className="absolute bottom-1 right-1 w-5 h-5 rounded-full text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: "#25D366" }} onClick={() => sendPhotoToChat(ph.file_id, p.name)} title="Send to chat">
                          <SendHorizontal size={11} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  p.image_url && <img src={p.image_url} alt={p.name} className="w-full h-24 object-cover rounded-lg mb-2 border" style={{ borderColor: "var(--cd-navy)" }} />
                )}
                {p.photos?.length > 0 && <div className="text-[10px] font-semibold text-slate-500 -mt-1 mb-1">↖ drag a photo into the chat to share it</div>}
                <div className="font-bold text-sm">{p.name}</div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-sm font-heading font-bold">{p.price ? `₹${p.price}` : ""}</span>
                  <span className={`status-badge ${p.stock_status === "in_stock" ? "badge-approved" : "badge-rejected"}`}>{p.stock_status === "in_stock" ? "in stock" : "out of stock"}</span>
                </div>
                {p.description && <div className="text-xs text-slate-600 mt-1 line-clamp-3">{p.description}</div>}
                {p.description?.length > 150 && <details className="text-xs"><summary className="font-bold cursor-pointer text-slate-500">Read full description</summary><div className="whitespace-pre-wrap mt-1 text-slate-600 max-h-48 overflow-y-auto">{p.description}</div></details>}
                {p.faqs && <details className="text-xs mt-1"><summary className="font-bold cursor-pointer">Quick info / FAQs</summary><div className="whitespace-pre-wrap mt-1 text-slate-600">{p.faqs}</div></details>}
              </div>
            ))}
          </>
        )}

        {tab === "orders" && (
          <>
            <div className="flex gap-1.5">
              <input data-testid="order-search-input" className="input-tactile py-1.5 text-sm" placeholder="Order # or phone..." value={orderQ} onChange={(e) => setOrderQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchOrders()} />
              <button data-testid="order-search-btn" className="btn-navy py-1.5 px-3" onClick={searchOrders} disabled={orderBusy}><Search size={15} /></button>
            </div>
            {orderBusy && <div className="font-hand text-lg text-amber-800 text-center">searching woo store...<div className="text-xs font-sans text-slate-500 mt-1">Order # is instant · phone search can take up to a minute</div></div>}
            {orderErr && <div className="text-xs font-semibold text-red-700 bg-red-50 border-2 border-red-200 rounded-lg p-2" data-testid="order-error">{orderErr}</div>}
            {orders && orders.length === 0 && <div className="font-hand text-lg text-amber-800 text-center">no orders found</div>}
            {(orders || []).map((o) => (
              <div key={o.id} className="card-tactile card-green p-3" data-testid={`order-card-${o.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-heading font-bold">#{o.number}</span>
                  <span className={`status-badge ${ORDER_COLORS[o.status] || "badge-pending"}`}>{o.status}</span>
                </div>
                <div className="text-xs text-slate-600">{o.customer_name} · {new Date(o.date).toLocaleDateString()}</div>
                <div className="mt-1.5 space-y-0.5">
                  {o.items.map((i, idx) => (
                    <div key={idx} className="text-xs flex justify-between"><span>{i.quantity}× {i.name}</span><span>₹{i.total}</span></div>
                  ))}
                </div>
                <div className="flex justify-between mt-1.5 pt-1.5 border-t font-bold text-sm" style={{ borderColor: "var(--cd-navy)" }}>
                  <span>Total</span><span>{o.currency === "INR" ? "₹" : o.currency}{o.total}</span>
                </div>
                {o.payment_method && <div className="text-[10px] text-slate-500 mt-1">{o.payment_method}</div>}
              </div>
            ))}
          </>
        )}
      </div>
      <OrderTemplateDialog conv={conv} order={updateOrder} open={!!updateOrder} onClose={() => setUpdateOrder(null)} />
    </div>
  );
}
